import React, { useContext, useEffect, useLayoutEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { decodeEntities } from "@wordpress/html-entities";
import { v4 as uuid } from "uuid";
import classes from "../../../scss/Post.module.scss";
import PreviewPost from "./PreviewPost";
import { FetchContext } from "../../../context/FetchContext";
import loaderClasses from "../../../components/commons/Loader.module.scss";
import gsap from "gsap";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";

gsap.registerPlugin(ScrollToPlugin);

gsap.config({
  force3D: false,
  nullTargetWarn: false,
  z: 0.1,
  rotationZ: 0.01,
  transformPerspective: 1000,
});

function getRandomSlugs(min, max, array) {
  let n = [];
  while (n.length < 3) {
    let slug = array[Math.floor(Math.random() * max) + min].slug;
    n.push(slug);
    n = [...new Set(n)];
  }
  return n.toString();
}

const Post = () => {
  const {
    store: {
      posts: {
        post: { data: post, loading: postLoading },
        slugs: { data: slugs },
        otherPosts: { data: otherPosts },
      },
    },
    getItem,
    clearPropNameData,
  } = useContext(FetchContext);

  const [randomSlugs, setRandomSlugs] = useState("");

  const { slug } = useParams();

  const postQuery = {
    _embed: "wp:term",
    _fields: "_links, custom_fields, title, content, slug, date",
    slug: slug,
  };

  const otherPostsQuery = {
    _embed: "wp:term",
    _fields: "_links, title,  slug, featured_media,img_url, date",
    slug: randomSlugs,
  };

  const slugsQuery = {
    _fields: "slug",
    per_page: 100,
  };

  const scrollTo = (id) => {
    gsap.to(window, {
      duration: 0.5,
      scrollTo: { y: `${id}`, offsetY: 100 },
    });
  };

  const scrollToAnchor = () => {
    const anchors = document.querySelectorAll(".post_anchor a");
    anchors.length &&
      anchors.forEach((anchor) => {
        const href = anchor.getAttribute("href");
        anchor.addEventListener("click", (e) => {
          e.preventDefault();
          scrollTo(href);
        });
      });
  };

  useLayoutEffect(() => {
    if (slug !== post[0]?.slug) {
      clearPropNameData("posts", "post");
    }
    clearPropNameData("posts", "otherPosts");
  }, [slug]);

  useEffect(() => {
    if (slug !== post[0]?.slug) {
      getItem({
        propName: "post",
        entity: "posts",
        query: postQuery,
      });
    }
  }, [slug]);

  useEffect(() => {
    post && scrollToAnchor();
  }, [post]);

  useEffect(() => {
    if (!slugs.length) {
      getItem({
        propName: "slugs",
        entity: "posts",
        query: slugsQuery,
      });
    }
  }, []);

  useEffect(() => {
    if (randomSlugs) {
      getItem({
        propName: "otherPosts",
        entity: "posts",
        query: otherPostsQuery,
      });
    }
  }, [randomSlugs]);

  useEffect(() => {
    if (slugs.length) {
      const array = slugs.filter((item) => item.slug !== slug);
      const min = 0;
      const max = array.length - 1;
      setRandomSlugs(getRandomSlugs(min, max, array));
    }
  }, [slugs, slug]);

  return (
    <div className={classes.post__page}>
      {postLoading ? <div className={loaderClasses.loader} /> : null}
      {post[0] ? (
        <div className={classes.post__wrapper}>
          <h1 className={classes.post__title}>{post[0].title.rendered}</h1>
          <div className={classes.post__body}>
            <div className={classes.post__text}>
              <div
                dangerouslySetInnerHTML={{
                  __html: decodeEntities(post[0].content.rendered),
                }}
              />
              <div className={classes.post__body_footer}>
                <div className={classes.post__body_footer_date}>
                  {new Date(post[0].date).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </div>
              </div>
              <div className={classes.post__tags_wrapper}>
                {post[0]?._embedded["wp:term"][1]?.map((tag) => (
                  <div className={classes.post__tag} key={uuid()}>
                    {tag.name}
                  </div>
                ))}
              </div>
            </div>
            <div className={classes.post__another_posts}>
              <h3 className={classes.post__another_posts_title}>
                Our other articles
              </h3>
              {otherPosts.length ? (
                <div className={classes.post__another_posts_body}>
                  {otherPosts.map((post) => (
                    <PreviewPost key={uuid()} post={post} />
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Post;
